/**
 * Host-based visibility gates for the deployed cpanel.
 *
 * Two gates share the hostname-matching convention:
 * - the "Посмотреть информацию" row action on `/merchants` is hidden only on
 *   the production origin (`cpanel-v2.noor.uz`), shown everywhere else
 *   (including dev `cpanel.test.noor.uz` and localhost);
 * - production keeps the same page visibility rules as other hosts, except
 *   for the explicitly hidden navigation links below.
 */

function normalizeHost(hostname?: string): string {
  const host =
    hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname);
  return host.trim().toLowerCase();
}

export const VENDOR_INFO_HIDDEN_HOSTS = ['cpanel-v2.noor.uz'] as const;

/** True when the vendor-info row action must be hidden for `hostname`. */
export function shouldHideVendorInfoAction(hostname?: string): boolean {
  return VENDOR_INFO_HIDDEN_HOSTS.includes(
    normalizeHost(hostname) as (typeof VENDOR_INFO_HIDDEN_HOSTS)[number],
  );
}

/** Production origin of the deployed cpanel. */
export const PROD_HOSTS = ['cpanel-v2.noor.uz'] as const;

/** True on the production origin (dev, previews, and localhost are not prod). */
export function isProdHost(hostname?: string): boolean {
  return (PROD_HOSTS as readonly string[]).includes(normalizeHost(hostname));
}

/**
 * True when a page is visible in production. The sign-in page is handled
 * separately by the router guard; every other page remains reachable.
 */
export const PROD_HIDDEN_PATHS = [] as const;

function matchesRoot(pathname: string, roots: readonly string[]): boolean {
  return roots.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

/** True when `pathname` is reachable on the production origin. */
export function isProdVisiblePath(pathname: string): boolean {
  return !matchesRoot(pathname, PROD_HIDDEN_PATHS);
}

/**
 * Sidebar-only production hiding: Диспетчеризация
 * (the search-service settings pages). The
 * routes themselves stay reachable (order detail is opened from chats, courier
 * detail from order pages) — only the nav links go, and a group left without
 * links disappears with them.
 */
export const PROD_HIDDEN_NAV_PATHS = [
  '/settings/delivery',
  '/settings/eta',
  '/settings/multiorder',
  '/settings/cities',
] as const;

/** True when a sidebar link to `to` is shown on the production origin. */
export function isProdNavVisible(to: string): boolean {
  return isProdVisiblePath(to) && !matchesRoot(to, PROD_HIDDEN_NAV_PATHS);
}
